import { InjectProperty } from '@jack-henry/frontend-utils/di';
import { DynamicComponentExitReason } from '@jack-henry/frontend-utils/lit/dynamic-component-registry';
import { TmBaseComponent } from '@treasury/presentation';
import { informationIcon } from '@treasury/presentation/assets/icons';
import { TmDialogService } from '@treasury/presentation/services/dialog';
import { css, html, nothing, PropertyValues } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { ResourceCenterItemVm } from '../data/resource-center-item.vm';

export const tagName = 'resource-center-item';
@customElement(tagName)
export class ResourceCenterItem extends TmBaseComponent {
    @property({ attribute: false })
    public resource?: ResourceCenterItemVm;

    @state()
    private tooltipOpened = false;

    @InjectProperty()
    declare private readonly dialogService: TmDialogService;

    protected render() {
        if (!this.resource) {
            return nothing;
        }

        return html` <div class="flex">
            <div class="flex items-center pb-2">
                <div
                    @click=${() => {
                        this.tooltipOpened = !this.tooltipOpened;
                    }}
                >
                    ${this.resource.logo
                        ? html`<img
                              src="data:image/jpeg;base64, ${this.resource.logo}"
                              alt=${this.resource.titleText}
                              class="h-6 w-6 mr-3"
                          />`
                        : html`<tm-icon .icon=${informationIcon}></tm-icon>`}
                </div>
                ${this.renderTooltip(this.resource)}
            </div>
        </div>`;
    }

    protected updated(_changedProperties: PropertyValues): void {
        if (_changedProperties.has('tooltipOpened') && this.tooltipOpened) {
            setTimeout(() => {
                this.tooltipOpened = false;
            }, 5000);
        }
    }

    private async showExternalWarningDialog(resource: ResourceCenterItemVm) {
        if (this.dialogService.isDialogOpen) {
            return;
        }

        const { externalWarningMessage, link } = resource;
        const { reason } = await this.dialogService.open(
            externalWarningMessage,
            'External Warning',
            {
                buttons: [
                    {
                        label: 'Continue',
                        reason: DynamicComponentExitReason.Confirm,
                    },
                ],
                classes: ['max-w-[700px]'],
            }
        );

        if (reason === DynamicComponentExitReason.Confirm) {
            window.open(link, '_blank');
        }
    }

    private renderLink(resource: ResourceCenterItemVm) {
        const { link, displayExternalWarning, title } = resource;
        return html` <a
            class="text-sm"
            href="javascript:void(0)"
            rel=${'noopener noreferrer'}
            @click=${() => {
                if (displayExternalWarning) {
                    this.showExternalWarningDialog(resource);
                } else {
                    window.open(link, '_blank');
                }
            }}
        >
            ${title}
        </a>`;
    }

    private renderTooltip(resource: ResourceCenterItemVm) {
        return resource.tooltip
            ? html`<tm-tooltip
                  .text=${resource.tooltip}
                  .position=${'top'}
                  .tooltipOpened=${this.tooltipOpened}
                  >${this.renderLink(resource)}</tm-tooltip
              >`
            : this.renderLink(resource);
    }

    public static get styles() {
        return css`
            :host {
                display: block;
            }
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: ResourceCenterItem;
    }
}
